export interface AuthenticatedUser {
    id: number;
    name: string;
    balance: number;
    isAdmin: boolean;
    stats?: {
        handsPlayed: number;
        bestHand: string;
        biggestWin: number;
        totalWon: number;
        totalLost: number;
    };
}

export class ApiAuth {
    private static instance: ApiAuth;
    private user: AuthenticatedUser | null = null;
    private apiKey: string | null = null;
    private adminServerUrl = window.location.hostname === 'localhost' ? 'http://localhost:3334' : 'https://slots.claudebuilt.me'; // Admin server URL

    private constructor() {}

    public static getInstance(): ApiAuth {
        if (!ApiAuth.instance) {
            ApiAuth.instance = new ApiAuth();
        }
        return ApiAuth.instance;
    }

    public async initialize(): Promise<boolean> {
        // Check for API key in URL first, then localStorage
        const urlParams = new URLSearchParams(window.location.search);
        let key = urlParams.get('key');

        if (!key) {
            // Try to get key from localStorage
            key = localStorage.getItem('apiKey');
        }

        if (!key) {
            this.showAuthError();
            return false;
        }

        try {
            const response = await fetch(`${this.adminServerUrl}/api/validate/${key}`);
            const data = await response.json();

            if (data.valid) {
                this.user = data.user;
                this.apiKey = key;

                // Store in both sessionStorage and localStorage for persistence
                sessionStorage.setItem('apiKey', key);
                sessionStorage.setItem('user', JSON.stringify(data.user));
                localStorage.setItem('apiKey', key);
                localStorage.setItem('user', JSON.stringify(data.user));

                // Clean URL (remove key from display but keep it in session)
                const cleanUrl = window.location.protocol + '//' + window.location.host + window.location.pathname;
                window.history.replaceState({}, document.title, cleanUrl);

                return true;
            } else {
                this.showAuthError(data.error || 'Invalid API key');
                return false;
            }
        } catch (error) {
            console.error('Auth error:', error);

            // Try to restore from session or localStorage if server is temporarily unavailable
            let storedKey = sessionStorage.getItem('apiKey');
            let storedUser = sessionStorage.getItem('user');

            if (!storedKey) {
                storedKey = localStorage.getItem('apiKey');
                storedUser = localStorage.getItem('user');
            }

            if (storedKey && storedUser) {
                this.apiKey = storedKey;
                this.user = JSON.parse(storedUser);
                return true;
            }

            this.showAuthError('Authentication server unavailable');
            return false;
        }
    }

    private showAuthError(message: string = 'Access denied. Please use a valid access link.') {
        // Create a full-screen auth error
        const overlay = document.createElement('div');
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 999999;
        `;

        const errorBox = document.createElement('div');
        errorBox.style.cssText = `
            background: white;
            padding: 40px;
            border-radius: 10px;
            text-align: center;
            max-width: 400px;
            box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        `;

        errorBox.innerHTML = `
            <h1 style="color: #333; margin-bottom: 20px;">🔒 Access Denied</h1>
            <p style="color: #666; margin-bottom: 30px;">${message}</p>
            <p style="color: #999; font-size: 14px;">Please contact the administrator for a valid access link.</p>
        `;

        overlay.appendChild(errorBox);
        document.body.appendChild(overlay);
    }

    public getUser(): AuthenticatedUser | null {
        return this.user;
    }

    public getApiKey(): string | null {
        return this.apiKey;
    }

    public async updateBalance(balance: number, won: number = 0, lost: number = 0): Promise<void> {
        if (!this.user || !this.apiKey) return;

        try {
            const response = await fetch(`${this.adminServerUrl}/api/users/${this.user.id}/balance`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': this.apiKey
                },
                body: JSON.stringify({ balance, won, lost })
            });

            if (response.ok) {
                this.user.balance = balance;
                sessionStorage.setItem('user', JSON.stringify(this.user));
            }
        } catch (error) {
            console.error('Failed to update balance:', error);
        }
    }

    public getAdminServerUrl(): string {
        return this.adminServerUrl;
    }
}